import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * Every font-size token in tokens.css, by the utility name Tailwind generates.
 *
 * tailwind-merge has to be told about these. It only knows Tailwind's stock scale
 * (`text-sm`, `text-lg`, …), so a custom `text-card-title` falls through to its
 * text-*colour* group — and `cn("text-card-title", "text-on-surface")` then drops
 * the size as a losing colour. That failure is silent and looks exactly like a
 * token that was never defined: the element simply inherits its parent's size.
 */
const FONT_SIZES = [
  "hero",
  "page-title",
  "record",
  "section-header",
  "card-title",
  "stat",
  "dial",
  "tile",
  "disc",
  "avatar",
  "lead",
  "field",
  "row",
  "body",
  "label",
  "data",
  "detail",
  "tab",
  "subtitle",
  "caption",
  "micro",
  "kicker",
  "chip",
  "label-caps",
  "nav-label",
  "tile-label",

  // The desktop steps.
  "page-title-wide",
  "brand",
  "panel-title",
  "tile-wide",
  "disc-wide",
  "task",
  "rail-label",
  "nav-item",
  "count",
  "badge",
  "cell",
  "note",
  "auth-title",
  "pitch",
  "lead-wide",

  // The Organic steps.
  "detail-lg",
  "tag",
  "tag-lg",
  "tab-label",
  "pill",
  "cta",
  "cta-sm",
  "sheet-title",
  "section-header-wide",
  "dial-wide",
  "dial-label",
  "tile-label-wide",
  "avatar-sm",
  "fact-label",
  "body-tight",
  "node",
  "node-sm",
  "node-sub",
  "pair-title",
  "day-hero",
  "step-title",
  "weekday",
  "day",
  "header-lg",
  "tagline",
  "record-wide",
  "cta-xs",
  "pair-wide",
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: FONT_SIZES }],
    },
  },
});

/**
 * Merges Tailwind classes, letting a later class win over an earlier one that sets
 * the same property. Without this, a caller passing `className="bg-error"` would end
 * up with two competing background classes and whichever CSS order decides.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
